import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { Search, Sparkles, ShieldCheck, ArrowRight, Zap, CheckCircle2, BookOpen, Layers, HelpCircle, Calculator } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { CALCULATOR_CATEGORIES, CALCULATORS, type CalculatorCategory } from "@/lib/calculators-catalog";
import { CalcIcon } from "@/components/calculators/CalcIcon";

export const Route = createFileRoute("/calculators/")({
  head: () => ({
    meta: [
      { title: "Free Online Calculators — 122+ Math, Financial, Health & Science Tools | Picamini.online" },
      {
        name: "description",
        content:
          "Explore 122+ free online calculators for math, finance, health, science, and education. 100% browser-based, instant formulas, step-by-step solutions, and zero data tracking.",
      },
      {
        name: "keywords",
        content:
          "online calculators, percentage calculator, mortgage calculator, bmi calculator, grade calculator, scientific notation calculator, free math tools, financial calculators",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Free Online Calculators — 122+ Instant Tools | Picamini.online" },
      {
        property: "og:description",
        content:
          "Fast, accurate, 100% client-side online calculators. Perform math, financial, fitness, science, and everyday calculations with full privacy.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/calculators" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Free Online Calculators Directory on Picamini.online" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Free Online Calculators — 122+ Instant Tools | Picamini.online" },
      { name: "twitter:description", content: "Explore 122+ free browser-based calculators for math, finance, health, and science." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/calculators" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/calculators" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/calculators" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/calculators" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/calculators" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/calculators" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/calculators" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Free Online Calculators Directory",
          "url": "https://picamini.online/calculators",
          "description": "Directory of 122+ free, private, client-side online calculators covering math, finance, fitness, science, and education.",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Picamini.online",
            "url": "https://picamini.online"
          },
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": CALCULATORS.length,
            "itemListElement": CALCULATORS.slice(0, 25).map((c, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": c.name,
              "url": `https://picamini.online/calculators/${c.slug}`,
              "description": c.tagline
            }))
          }
        })
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Are all calculators on Picamini.online 100% free to use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, every calculator on Picamini.online is completely free with no usage limits, no credit card requirements, and no registration needed."
              }
            },
            {
              "@type": "Question",
              "name": "Is my data stored or sent to a remote server?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No. All computations run 100% locally inside your web browser using modern JavaScript and WebAssembly. Your numbers, financial figures, and private metrics never leave your device."
              }
            },
            {
              "@type": "Question",
              "name": "Can I use these calculators on my smartphone or tablet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. Every calculator is designed to be fully mobile-responsive and functions smoothly on iOS, Android, macOS, Windows, and Linux devices."
              }
            }
          ]
        })
      }
    ]
  }),
  component: CalculatorsHub,
});

export function CalculatorsHub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CalculatorCategory | "All">("All");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const catParam = params.get("cat");
      const qParam = params.get("q");
      if (catParam && CALCULATOR_CATEGORIES.includes(catParam as CalculatorCategory)) {
        setCategory(catParam as CalculatorCategory);
      }
      if (qParam) {
        setQuery(qParam);
      }
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CALCULATORS.filter((c) => {
      const matchesCategory = category === "All" || c.category === category;
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.tagline.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* Hero Section */}
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur mb-4">
              <ShieldCheck className="size-4 text-emerald-300" />
              100% Free & Private — Instant Browser Calculations
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Free Online Calculators
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed">
              Fast, accurate, and easy-to-use online calculators for students, teachers, and everyday math.
              Calculate percentages, weighted grades, exam scores, and GPAs instantly.
            </p>

            {/* Live Search Bar */}
            <div className="mx-auto mt-8 max-w-xl">
              <div className="relative flex items-center">
                <Search className="absolute left-4 size-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search calculators (e.g. percentage, grade, GPA)..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-13 pl-12 pr-4 rounded-2xl bg-background/95 text-foreground shadow-lg backdrop-blur text-sm sm:text-base border-white/20 focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Section */}
        <section className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 pb-4">
            <Button
              variant={category === "All" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("All")}
              className="rounded-full text-xs font-bold"
            >
              All Calculators ({CALCULATORS.length})
            </Button>
            {CALCULATOR_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat)}
                className="rounded-full text-xs font-bold"
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Calculator Grid */}
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((calc) => (
              <Link
                key={calc.slug}
                to={`/calculators/${calc.slug}`}
                className="group relative flex flex-col justify-between rounded-3xl border border-border/80 bg-card p-6 shadow-xs transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <CalcIcon name={calc.icon} className="size-6" />
                    </div>
                    {calc.featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Sparkles className="size-3" /> Featured
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-foreground transition-colors group-hover:text-primary">
                    {calc.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {calc.tagline}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4 text-xs font-bold text-primary">
                  <span>Open Calculator</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-bold text-foreground">No calculators found</p>
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your search query or filter category.</p>
            </div>
          )}
        </section>

        {/* ============ Rich SEO Informational Section ============ */}
        <section className="border-t border-border/60 bg-secondary/15 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              <header className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Accurate, Free & Client-Side Calculation Utilities
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  The Comprehensive Suite of Free Online Calculators
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Whether you are solving complex mathematical equations, managing personal loan repayments, evaluating employee retention costs, or tracking your fitness goals, Picamini.online delivers <strong>122+ free, dedicated calculators</strong> built for students, educators, professionals, and daily problem solvers.
                </p>
              </header>

              {/* Value Pillars */}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 not-prose">
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">100% Client-Side Privacy</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    All calculations process inside your web browser. No confidential financial figures or personal metrics are transmitted to any server.
                  </p>
                </div>

                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <Zap className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Instant Real-Time Results</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Results update live as you type, complete with step-by-step mathematical breakdowns, formulas, and visual comparison charts.
                  </p>
                </div>

                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">No Signup or Limits</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Every tool is unlocked with unlimited calculations, clean UI, copyable results, and zero paywalls or intrusive interstitials.
                  </p>
                </div>
              </div>

              {/* Calculator Categories Deep Dive */}
              <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 shadow-xs space-y-6">
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Explore Calculation Categories
                </h3>
                <div className="grid gap-6 sm:grid-cols-2 text-sm text-muted-foreground">
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                      <BookOpen className="size-4 text-primary" /> Math & Algebra Tools
                    </h4>
                    <p>
                      Solve linear equations, compute quadratic formulas, convert scientific notation, find LCM and GCF, calculate fraction percentages, and evaluate exponent expressions effortlessly.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                      <Layers className="size-4 text-primary" /> Financial & Business Calculators
                    </h4>
                    <p>
                      Forecast loan schedules with our mortgage calculator, compute compound and simple interest, determine freelance hourly rates, analyze customer acquisition cost (CAC), and calculate working capital.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                      <Calculator className="size-4 text-primary" /> Health & Everyday Fitness
                    </h4>
                    <p>
                      Accurately calculate Body Mass Index (BMI), Total Daily Energy Expenditure (TDEE), target heart rate zones, daily macronutrient splits, and healthy calorie surpluses or deficits.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-bold text-foreground flex items-center gap-2">
                      <Sparkles className="size-4 text-primary" /> Education & Grading Systems
                    </h4>
                    <p>
                      Track semester GPA, calculate weighted assignment grades, determine needed final exam scores, and convert letter grades into standard 4.0 scale marks.
                    </p>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="space-y-6">
                <h3 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
                  <HelpCircle className="size-6 text-primary" /> Frequently Asked Questions
                </h3>
                <div className="space-y-4">
                  <div className="rounded-xl border border-border/80 bg-card p-5">
                    <h4 className="font-semibold text-foreground">Are these online calculators free for commercial and classroom use?</h4>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      Yes. Students, teachers, researchers, businesses, and freelancers can use all Picamini calculators freely without licensing fees or attribution requirements.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/80 bg-card p-5">
                    <h4 className="font-semibold text-foreground">How accurate are the mathematical formulas used?</h4>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      Our calculators implement industry-standard scientific, financial, and educational formulas (such as the standard amortization formula, Mifflin-St Jeor equation, and ISO measurement conversions) verified by domain specialists.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/80 bg-card p-5">
                    <h4 className="font-semibold text-foreground">Can I save or export my calculation results?</h4>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      Most of our calculators include one-click "Copy Result" or printable breakdown tables, enabling you to export summary tables into assignments, spreadsheets, or client proposals.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
